package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.BoxSizing
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxSizing
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/*
 * Numeric pager shared by the paginated lists. The visible window is `current ± 2`, plus the
 * first/last page when they fall outside it, with an ellipsis only when there is a real hidden
 * gap (page-3 exists and is not the first page, or page+3 exists and is not the last page).
 */

internal sealed interface PaginationItem {
    data class Page(val number: Int) : PaginationItem
    data object Ellipsis : PaginationItem
}

/** Number of pages for [total] items, or 0 when there is nothing to paginate. */
internal fun pageCountOf(total: Int, pageSize: Int): Int {
    if (total <= 0 || pageSize <= 0) return 0
    return (total - 1) / pageSize + 1
}

/** Slice for [page], clamped into `1..pageCount` so live updates that shrink the list never blank it. */
internal fun <T> List<T>.pageSlice(page: Int, pageSize: Int): List<T> {
    if (isEmpty() || pageSize <= 0) return emptyList()
    val pages = pageCountOf(size, pageSize)
    val safePage = page.coerceIn(1, pages)
    val from = (safePage - 1) * pageSize
    return subList(from, minOf(from + pageSize, size))
}

/** Items of the pager, without placeholders for pages that do not exist. */
internal fun paginationItems(current: Int, pageCount: Int): List<PaginationItem> {
    if (pageCount <= 1) return emptyList()
    val page = current.coerceIn(1, pageCount)
    val start = maxOf(1, page - 2)
    val end = minOf(pageCount, page + 2)
    val items = mutableListOf<PaginationItem>()

    if (start > 1) {
        items += PaginationItem.Page(1)
        // Only a real gap (pages 2..start-1) gets an ellipsis; a page-3 that is the first page does not.
        if (page - 3 > 1) items += PaginationItem.Ellipsis
    }
    for (number in start..end) items += PaginationItem.Page(number)
    if (end < pageCount) {
        if (page + 3 < pageCount) items += PaginationItem.Ellipsis
        items += PaginationItem.Page(pageCount)
    }
    return items
}

val PaginationStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .gap(0.25.cssRem)
        .padding(0.75.cssRem, 1.25.cssRem)
}

/** Header variant: no padding, so the pager shares the panel-title line. */
val PaginationCompactStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .gap(0.25.cssRem)
}

val PaginationButtonStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.InlineBlock)
        // Raw buttons are border-box by default while the current-page span is not; keep both
        // boxes identical so minWidth + padding produce the same size.
        .boxSizing(BoxSizing.BorderBox)
        .minWidth(2.cssRem)
        // Bigger hit target than the original 1.75rem × ~20px control.
        .padding(0.3125.cssRem, 0.5.cssRem)
        // Raw buttons inherit the page font through the global `button { font: inherit }` rule.
        // The current-page span must use the same size explicitly to stay aligned with them.
        .fontSize(0.875.cssRem)
        .textAlign(TextAlign.Center)
        .color(IndustrialPalette.Ink)
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .cursor(Cursor.Pointer)
        .styleModifier { property("font-family", "inherit") }
}

val PaginationCurrentStyle = PaginationButtonStyle.extendedByBase {
    Modifier
        .backgroundColor(IndustrialPalette.Ink)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Ink)
        .color(IndustrialPalette.White)
        .cursor(Cursor.Default)
}

val PaginationEllipsisStyle = CssStyle.base {
    Modifier
        .padding(0.3125.cssRem, 0.25.cssRem)
        .fontSize(0.875.cssRem)
        .color(IndustrialPalette.Muted)
}

@Composable
internal fun Pagination(
    page: Int,
    pageCount: Int,
    onPage: (Int) -> Unit,
    compact: Boolean = false,
) {
    if (pageCount <= 1) return
    val current = page.coerceIn(1, pageCount)
    val style = if (compact) PaginationCompactStyle else PaginationStyle

    Div(attrs = style.toAttrs {
        attr("role", "navigation")
        attr("aria-label", "Paginação")
    }) {
        for (item in paginationItems(current, pageCount)) {
            when (item) {
                PaginationItem.Ellipsis -> Span(attrs = PaginationEllipsisStyle.toAttrs()) { Text("…") }
                is PaginationItem.Page -> {
                    if (item.number == current) {
                        Span(
                            attrs = PaginationCurrentStyle.toAttrs {
                                attr("aria-current", "page")
                                attr("aria-label", "Página ${item.number}")
                            },
                        ) {
                            Text(item.number.toString())
                        }
                    } else {
                        Button(
                            attrs = PaginationButtonStyle.toAttrs {
                                attr("type", "button")
                                attr("aria-label", "Ir para página ${item.number}")
                                onClick { onPage(item.number) }
                            },
                        ) {
                            Text(item.number.toString())
                        }
                    }
                }
            }
        }
    }
}
